import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';

const AuthContext = createContext(null);

// Token refresh interval (refresh 2 minutes before expiry)
const TOKEN_REFRESH_MARGIN_MS = 2 * 60 * 1000; // 2 minutes
const ACCESS_TOKEN_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes (matching backend)

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken || isRefreshingRef.current) {
      return false;
    }

    isRefreshingRef.current = true;
    
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: storedRefreshToken
      });
      
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Schedule next refresh
      scheduleTokenRefresh();
      
      console.log('Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout the user
      logout();
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Schedule the next token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule refresh 2 minutes before token expires
    const refreshTime = ACCESS_TOKEN_EXPIRY_MS - TOKEN_REFRESH_MARGIN_MS;
    
    refreshTimeoutRef.current = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
  }, [refreshAccessToken]);

  // Set up axios interceptor for handling 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If we get a 401 and haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // Retry the original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
            return axios(originalRequest);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  // Set up axios default header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check token and get user on mount
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data);
          // Schedule token refresh if we have a refresh token
          if (localStorage.getItem('refresh_token')) {
            scheduleTokenRefresh();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          // Try to refresh token before giving up
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            logout();
          } else {
            // Retry getting user info
            try {
              const response = await axios.get(`${API_URL}/auth/me`);
              setUser(response.data);
            } catch {
              logout();
            }
          }
        }
      }
      setLoading(false);
    };
    initAuth();
    
    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      // Check if 2FA is required
      if (response.data.requires_totp) {
        return { 
          success: false, 
          requires_totp: true, 
          temp_token: response.data.temp_token,
          message: response.data.message 
        };
      }
      
      const { access_token, refresh_token: newRefreshToken, token: legacyToken, user: userData } = response.data;
      
      const newToken = access_token || legacyToken;
      
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
        setRefreshToken(newRefreshToken);
      }
      setToken(newToken);
      setUser(userData);
      
      // Schedule token refresh
      if (newRefreshToken) {
        scheduleTokenRefresh();
      }
      
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const verifyTotp = async (tempToken, code) => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-totp`, { 
        temp_token: tempToken, 
        code: code 
      });
      
      const { access_token, refresh_token: newRefreshToken, token: legacyToken, user: userData } = response.data;
      
      const newToken = access_token || legacyToken;
      
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
        setRefreshToken(newRefreshToken);
      }
      setToken(newToken);
      setUser(userData);
      
      // Schedule token refresh
      if (newRefreshToken) {
        scheduleTokenRefresh();
      }
      
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const logout = () => {
    // Clear refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      // Update user state
      setUser(prev => ({ ...prev, must_change_password: false }));
      
      return { success: true };
    } catch (error) {
      let message = 'Password change failed';
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as an array
        message = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        message = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    changePassword,
    isAuthenticated: !!user,
    refreshAccessToken,
    verifyTotp,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
